module Meevo.Plugins.Location {
    'use strict';

    import Location = Meevo.Model.Location;
    import LocationStatusEnum = Meevo.Model.LocationStatusEnum;
    import SubscriptionStatusEnum = Meevo.Model.SubscriptionStatusEnum;
    import ClientMembershipBillStatusEnum = Meevo.Model.ClientMembershipBillStatusEnum;
    import BusinessInfoSettingEnum = Meevo.Model.BusinessInfoSettingEnum;
    import SecurityKeys = Meevo.Components.Common.SecurityKeys;

    interface IStatus {
        label: string;
        value: LocationStatusEnum | ClientMembershipBillStatusEnum;
    }

    /**
    * Main Section of Location Plugin
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<Location> {
        public businessType;
        public marketPricing;
        public todayDate = new Date();
        public savedLocationStatus: LocationStatusEnum;
        public statusList: Array<IStatus> = [
            {
                label: 'LABEL_ACTIVE',
                value: LocationStatusEnum.Active
            }, {
                label: 'LABEL_CLOSED',
                value: LocationStatusEnum.Closed
            }, {
                label: 'LABEL_TEMPORARILY_CLOSED',
                value: LocationStatusEnum.TemporarilyClosed
            }, {
                label: 'LABEL_CLOSING_PLANNED',
                value: LocationStatusEnum.ClosingPlanned
            }
        ];
        public statusEftList: Array<IStatus> = [
            {
                label: 'LABEL_WAIVED',
                value: ClientMembershipBillStatusEnum.WaivedAccrued
            }, {
                label: 'LABEL_NOT_BILLED',
                value: ClientMembershipBillStatusEnum.NotBilled
            }
        ];
        public settingNames = {
            [BusinessInfoSettingEnum.EnableMembershipBilling]: 'LABEL_DISABLE_MEMBERSHIP',
            [BusinessInfoSettingEnum.EnableAppointmentNotification]: 'LABEL_ENABLE_APPOINTMENT',
            [BusinessInfoSettingEnum.EnableAutoBillingService]: 'LABEL_ENABLE_AUTOMATIC_BILLING',
            [BusinessInfoSettingEnum.EnableOnlineBooking]: 'LABEL_ENABLE_ONLINE_BOOKING',
            [BusinessInfoSettingEnum.EnableOnlineWalkinManager]: 'LABEL_ENABLE_WALKIN_MANAGEMENT',
            [BusinessInfoSettingEnum.EnableThirdPartyIntegration]: 'LABEL_ENABLE_INTEGRATIONS'
        };

        constructor(
            sectionId: number,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected $q: angular.IQService,
            protected modalService: Meevo.Components.Modal.IModalService,
            sectionSecurityKey?: string
        ) {
            super(sectionId, displayNameContentKey, template, isPrimarySection, handlesOwnCrud, baseEndpoint, sectionEndpoint, loggingService, dataService, identityService, noun, null, sectionSecurityKey);
        }

        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        createSectionModelInstance(postAdd: (data: any) => any): void { }

        sectionModelRetrieved() {
            this.businessType = {
                displayName: this.model.businessTypeDisplayName,
                id: this.model.businessTypeId
            };

            this.marketPricing = {
                displayName: this.model.priceGroupDisplayName,
                id: this.model.priceGroup_TId
            }

            this.model.settings.forEach(s => {
                s.effectiveValue = Number(s.effectiveValue);
                if (s.securityKey) {
                    s.securityKey = s.securityKey.replace(':READ', '');
                } else {
                    s.securityKey = SecurityKeys.MANAGEMENT_LOCATION_TAB_MAIN;
                }
            });

            this.model.statusEft = _.find(this.model.settings, s => s.id === BusinessInfoSettingEnum.EFTToBeProcessedStatusEnum);
            this.model.enabledAutomaticBillingService = _.find(this.model.settings, s => s.id === BusinessInfoSettingEnum.EnableAutoBillingService);
            this.model.enabledMembershipProcesses = _.find(this.model.settings, s => s.id === BusinessInfoSettingEnum.EnableMembershipBilling);
            this.model.enableAppointmentNotification = _.find(this.model.settings, s=> s.id === BusinessInfoSettingEnum.EnableAppointmentNotification);
            this.model.enableOnlineWalkinManager = _.find(this.model.settings, s=> s.id === BusinessInfoSettingEnum.EnableOnlineWalkinManager);
            this.model.enableOnlineBooking = _.find(this.model.settings, s=> s.id === BusinessInfoSettingEnum.EnableOnlineBooking);
            this.model.enableThirdPartyIntegration = _.find(this.model.settings, s=> s.id === BusinessInfoSettingEnum.EnableThirdPartyIntegration);

            this.savedLocationStatus = this.model.locationStatus;

            this.model.address = {
                address1: this.model.address1,
                address2: this.model.address2,
                city: this.model.city,
                country: this.model.country,
                state: this.model.state,
                zipCode: this.model.zipCode
            }
        }

        preSave(): void {
            if (this.model.locationStatus !== LocationStatusEnum.TemporarilyClosed) {
                this.model.locationReactivationDate = null;
            }

            if (this.model.locationStatus !== LocationStatusEnum.Closed && this.model.locationStatus !== LocationStatusEnum.ClosingPlanned) {
                this.model.locationClosedDate = null;
            }

            this.model['businessInfos'] = [{
                address1: this.model.address.address1,
                address2: this.model.address.address2,
                state: this.model.address.state,
                city: this.model.address.city,
                zipCode: this.model.address.zipCode,
                country: this.model.address.country,
                latitude: this.model.latitude,
                longitude: this.model.longitude,
                locationId: this.model.locationId,
                taxNumber: this.model.taxNumber,
                storeName: this.model.storeName,
                storeNumber: this.model.storeNumber,
                customerNumber: this.model.customerNumber,
                countryCode: this.model.countryCode,
                phoneNumber: this.model.phoneNumber,
                emailAddress: this.model.emailAddress,
                webAddressUrl: this.model.webAddressUrl,
                businessTypeEnum: this.model.businessTypeEnum,
                cultureInfo: this.model.cultureInfo,
                areaSalesManager: this.model.areaSalesManager,
                areaInstallManager: this.model.areaInstallManager,
                entityVersion: this.model.businessInfoEntityVersion
            }];

            this.model['locations'] = [{
                locationId: this.model.locationId,
                storeNumber: this.model.storeNumber,
                storeName: this.model.storeName,
                externalId: this.model.externalId,
                customerNumber: this.model.customerNumber,
                defaultLanguageId: this.model.defaultLanguageId,
                locationTimeZone: this.model.locationTimeZone,
                //priceGroup_TId: this.model.priceGroup_TId,
                priceGroup_TId: this.marketPricing.id,
                salesForceId: this.model.salesForceId,
                subDomainUrl: this.model.subDomainUrl,
                meevoEditionType: this.model.meevoEditionType,
                maxServiceProvidersAllowed: this.model.maxServiceProvidersAllowed,
                subscriptionStatus: this.model.subscriptionStatus,
                subscriptionActivationDate: this.model.subscriptionActivationDate,
                subscriptionTerminationDate: this.model.subscriptionTerminationDate,
                locationStatus: this.model.locationStatus,
                locationReactivationDate: this.model.locationReactivationDate,
                locationClosedDate: this.model.locationClosedDate,

                includeDiscount: this.model.includeDiscount,
                includeClientVisitNoteType: this.model.includeClientVisitNoteType,
                includeGiftCardType: this.model.includeGiftCardType,
                includeReferralType: this.model.includeReferralType,

                entityVersion: this.model.entityVersion,
                isCentralOffice: false,

                editionPlanId: this.model.editionPlanId,
                smsPlanId: this.model.smsPlanId,
                supportPlanId: this.model.supportPlanId
            }];

            this.model['locationBusinessTypes'] = [{
                locationId: this.model.locationId,
                businessType_TId: this.businessType.id,
                isPrimary: 1
            }];

            if (this.model.enabledAutomaticBillingService.effectiveValue) {
                this.model.enabledMembershipProcesses.effectiveValue = 1;
                this.model.statusEft.effectiveValue = ClientMembershipBillStatusEnum.NotBilled;
            }

            if (!this.model.enabledMembershipProcesses.effectiveValue) {
                this.model.statusEft.effectiveValue = ClientMembershipBillStatusEnum.NotBilled;
            }

            this.model['businessSettings'] = this.model.settings.map(s => {
                return {
                    value: +s.effectiveValue,
                    settingSid: s.id
                }
            });
        }

        stateChanged(): void {
            switch (this.model.locationStatus) {
                case LocationStatusEnum.TemporarilyClosed:
                    this.model.settings.filter(s => {
                        return s.id === BusinessInfoSettingEnum.EnableAutoBillingService || s.id == BusinessInfoSettingEnum.EnableOnlineBooking || s.id === BusinessInfoSettingEnum.EnableOnlineWalkinManager
                    }).forEach(s => {
                        s.effectiveValue = 0;
                    });
                    break;
                case LocationStatusEnum.Closed:
                    if (this.savedLocationStatus !== LocationStatusEnum.Closed) {
                        this.model.locationClosedDate = new Date();
                    }

                    this.model.settings.forEach(s => {
                        if (s.id !== BusinessInfoSettingEnum.EFTToBeProcessedStatusEnum && s.id !== BusinessInfoSettingEnum.EnableMembershipBilling) {
                            s.effectiveValue = 0;
                        }
                    });
                    break;
                case LocationStatusEnum.Active:
                    this.model.settings.forEach(s => {
                        if (s.id !== BusinessInfoSettingEnum.EFTToBeProcessedStatusEnum && s.id !== BusinessInfoSettingEnum.EnableMembershipBilling) {
                            s.effectiveValue = 1;
                        }
                    });
                    break;
            }
        }

        locationClosedDateChanged(): void {
            if (this.model.locationStatus === LocationStatusEnum.Closed && moment(this.model.locationClosedDate).isAfter()) {
                this.model.locationStatus = LocationStatusEnum.ClosingPlanned;
            }
        }

        confirmSave(): ng.IPromise<any> {
            let defer = this.$q.defer();

            if (this.originalEntity.locationStatus === LocationStatusEnum.Active && this.model.locationStatus !== LocationStatusEnum.Active) {
                this.busyService.stop();
                this.modalService.confirm('MESSAGE_ONLY_FULL_ACCESS_USERS_WILL_HAVE_ACCESS_TO_CLOSED_LOCATION', Meevo.Components.Modal.ModalTranslationModes.Translate)
                    .then(() => {
                        this.busyService.start();
                        defer.resolve();
                    }, () => {
                        defer.reject(null);
                    });
            } else {
                defer.resolve();
            }

            return defer.promise;
        }

        enabledAutomaticBillingServiceChanged(): void {
            if (!this.model.enabledAutomaticBillingService.effectiveValue) {
                this.model.enabledMembershipProcesses.effectiveValue = 1;
                this.model.statusEft.effectiveValue = ClientMembershipBillStatusEnum.WaivedAccrued;
            }
        }

        get locationStatusEnum(): any {
            return LocationStatusEnum;
        }

        get subscriptionStatusEnum(): any {
            return SubscriptionStatusEnum;
        }

        get businessInfoSettingEnum(): any {
            return BusinessInfoSettingEnum;
        }
    }
}